import { useQuery } from "@tanstack/react-query";
import { Award } from "lucide-react";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { api, type CertAppStatus, type CertRow } from "@/lib/api";

const METHOD_BADGE: Record<string, { label: string; variant: "success" | "warning" | "secondary" | "outline" }> = {
  letsencrypt: { label: "Let's Encrypt", variant: "success" },
  custom: { label: "custom cert", variant: "warning" },
  none: { label: "HTTP only", variant: "secondary" },
};

export default function Certificates() {
  const { data } = useQuery({
    queryKey: ["certificates"],
    queryFn: () => api.get<{ certificates: CertRow[]; renewal_jobs: { name?: string; status?: string; [k: string]: unknown }[] }>("/api/certificates"),
    refetchInterval: 60_000,
  });
  const certs = data?.certificates ?? [];
  const jobs = data?.renewal_jobs ?? [];

  // nx.certs — native cert-lifecycle view (module off → 409 → hide quietly)
  const { data: nx } = useQuery({
    queryKey: ["nx-cert-status"],
    queryFn: () => api.get<{ apps: CertAppStatus[]; letsencrypt_jobs: { success?: boolean | null; detail?: string; error?: string } }>("/api/certs/status")
      .catch(() => null),
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Certificates"
        description="Per-domain TLS state: issuance method, app binding and the scheduler's real Let's Encrypt renewal jobs. An app on `none` is HTTP-only — visible, not assumed."
      />
      <Card>
        <Table>
          <THead>
            <TR><TH>Domain</TH><TH>Application</TH><TH>Issuance</TH><TH>App status</TH></TR>
          </THead>
          <TBody>
            {certs.map((c) => (
              <TR key={c.domain + c.app_id}>
                <TD className="font-medium">{c.domain}</TD>
                <TD className="text-xs text-muted-foreground">{c.app}</TD>
                <TD><Badge variant={METHOD_BADGE[c.method]?.variant ?? "secondary"}>
                  {METHOD_BADGE[c.method]?.label ?? c.method}</Badge></TD>
                <TD className="text-xs">{c.status}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {certs.length === 0 && (
          <EmptyState icon={Award} title="No applications yet"
            hint="Certificate state appears once an application is onboarded." />
        )}
      </Card>

      <Card>
        <div className="border-b px-4 py-3 text-sm font-medium">Scheduler renewal jobs</div>
        <Table>
          <THead><TR><TH>Job</TH><TH>Detail</TH></TR></THead>
          <TBody>
            {jobs.map((j, i) => (
              <TR key={i}>
                <TD className="font-mono text-xs">{String(j.name ?? j.plugin ?? "renewal")}</TD>
                <TD className="max-w-[400px] truncate font-mono text-[11px] text-muted-foreground">
                  {JSON.stringify(j).slice(0, 160)}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {jobs.length === 0 && (
          <div className="px-4 py-6 text-sm text-muted-foreground">
            No LE jobs reported by the scheduler (AUTO_LETS_ENCRYPT off, or no LE apps).
          </div>
        )}
      </Card>

      {nx && (
        <Card>
          <div className="border-b px-4 py-3 text-sm font-medium">
            Native cert checks <span className="text-xs font-normal text-muted-foreground">nx.certs — worker SNI probes + renewal state</span>
          </div>
          <Table>
            <THead><TR><TH>Application</TH><TH>Method</TH><TH>Wildcards</TH><TH>Expires</TH><TH>Renewal</TH></TR></THead>
            <TBody>
              {nx.apps.map((a) => (
                <TR key={a.app_id}>
                  <TD className="font-medium">{a.name}</TD>
                  <TD className="text-xs">{a.cert_method}</TD>
                  <TD className="font-mono text-xs">{a.wildcards.join(" ") || "—"}</TD>
                  <TD className="text-xs">
                    {a.state?.days_left != null
                      ? `${a.state.days_left}d`
                      : "—"}
                  </TD>
                  <TD>
                    <Badge variant={a.state?.error || a.state?.renewal_status === "renewal-failed" ? "destructive"
                      : a.state?.renewal_status === "ok" ? "success" : "secondary"}>
                      {a.state?.renewal_status ?? "unchecked"}
                    </Badge>
                    {a.state?.error && <span className="ml-2 text-xs text-destructive">{a.state.error}</span>}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
